import { useState, useEffect } from 'react';

import styles from './ContentEditor.module.css';
import EmbeddedCategoryManager from './EmbeddedCategoryManager';

/**
 * ImageGalleryEditor Component
 *
 * Editor for image-gallery page type
 * Manages description and display settings
 *
 * Note: Actual images are managed through the Image Manager
 */

/**
 *
 */
type ImageGalleryContent = {
  readonly description?: string;
  readonly settings?: {
    readonly imagesPerLoad?: number;
    readonly enableCategories?: boolean;
  };
};

/**
 *
 */
type ImageGalleryEditorProps = {
  readonly slug: string;
  readonly pageTitle: string;
  readonly initialContent: Record<string, unknown>;
  readonly onSave: (content: Record<string, unknown>) => Promise<void>;
  readonly isSaving: boolean;
};

const ImageGalleryEditor = ({
  slug: _slug,
  pageTitle,
  initialContent,
  onSave,
  isSaving,
}: ImageGalleryEditorProps): JSX.Element => {
  const [description, setDescription] = useState('');
  const [imagesPerLoad, setImagesPerLoad] = useState(10);
  const [enableCategories, setEnableCategories] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form from content
  useEffect(() => {
    const content = initialContent as ImageGalleryContent;
    setDescription(content.description ?? '');
    setImagesPerLoad(content.settings?.imagesPerLoad ?? 10);
    setEnableCategories(content.settings?.enableCategories ?? true);
  }, [initialContent]);

  // Track changes
  useEffect(() => {
    const content = initialContent as ImageGalleryContent;
    const hasChanged =
      description !== (content.description ?? '') ||
      imagesPerLoad !== (content.settings?.imagesPerLoad ?? 10) ||
      enableCategories !== (content.settings?.enableCategories ?? true);
    setHasChanges(hasChanged);
  }, [description, imagesPerLoad, enableCategories, initialContent]);

  const handleSave = async () => {
    const content: ImageGalleryContent = {
      description,
      settings: {
        imagesPerLoad,
        enableCategories,
      },
    };

    try {
      await onSave(content);
      setHasChanges(false);
    } catch (err) {
      // Error handled by parent
    }
  };

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorHeader}>
        <div>
          <h1 className={styles.editorTitle}>{pageTitle}</h1>
          <p className={styles.editorSubtitle}>Image Gallery Settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={styles.saveButton}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={styles.editorContent}>
        {/* Description Section */}
        <div className={styles.section}>
          <label className={styles.label} htmlFor="description">
            Gallery Description
          </label>
          <p className={styles.helpText}>Optional description shown at the top of the gallery</p>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={styles.textarea}
            rows={4}
            placeholder="Enter gallery description..."
          />
        </div>

        {/* Settings Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Display Settings</h2>

          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="imagesPerLoad">
              Images Per Load
            </label>
            <p className={styles.helpText}>
              Number of images to load when scrolling the gallery ring
            </p>
            <input
              id="imagesPerLoad"
              type="number"
              min="1"
              max="50"
              value={imagesPerLoad}
              onChange={(e) => setImagesPerLoad(parseInt(e.target.value) || 10)}
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={enableCategories}
                onChange={(e) => setEnableCategories(e.target.checked)}
              />
              <span>Enable Category Filtering</span>
            </label>
            <p className={styles.helpText}>
              Allow users to filter images by category (categories managed in Category Manager)
            </p>
          </div>
        </div>

        {/* Info Section */}
        <div className={styles.infoBox}>
          <div className={styles.infoIcon}>ℹ️</div>
          <div>
            <p className={styles.infoTitle}>Managing Images</p>
            <p className={styles.infoText}>
              Images are uploaded and managed through the{' '}
              <a href="/admin/images" className={styles.link}>
                Image Manager
              </a>
              . Assign images to categories with type "portfolio" to display them in this gallery.
            </p>
          </div>
        </div>

        {/* Category Manager Section */}
        {enableCategories ? <EmbeddedCategoryManager type="portfolio" /> : null}
      </div>
    </div>
  );
};

export default ImageGalleryEditor;
